import React from 'react';
import { DocumentTextIcon, PrinterIcon, ClockIcon, CheckCircleIcon, BookOpenIcon, SparklesIcon } from '@heroicons/react/24/outline';

const PrintPWNotes: React.FC = () => {
  return (
    <article className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-purple-400 to-secondary bg-clip-text text-transparent leading-tight">
          How to Print PW Notes in White Background Free 100% | #PW #PhysicsWallah #Unacademy #eSaral #ALLEN
        </h1>
        <p className="text-xl text-text-muted leading-relaxed">
          Save 20+ hours per week by converting EdTech lecture PDFs to clean, printable notes instantly — 100% free!
        </p>
      </div>

      {/* The Problem Section */}
      <div className="bg-glass/30 backdrop-blur-sm border border-red-500/30 rounded-2xl p-8 mb-12">
        <h2 className="text-3xl font-bold mb-6 text-red-400 flex items-center gap-3">
          <ClockIcon className="w-8 h-8" />
          The Note-Making Challenge Every Student Faces
        </h2>
        
        <div className="space-y-6">
          <p className="text-lg text-text-muted leading-relaxed">
            If you're using Physics Wallah, Unacademy, eSaral, ALLEN, or any EdTech platform, you've experienced this:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-glass/20 border border-border-glow/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <ClockIcon className="w-8 h-8 text-text-main flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-text-main mb-2">Time-Intensive Process</h3>
                  <p className="text-text-muted">A 2-hour lecture often turns into 4 hours of note-making, consuming your entire study schedule.</p>
                </div>
              </div>
            </div>

            <div className="bg-glass/20 border border-border-glow/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <SparklesIcon className="w-8 h-8 text-text-main flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-text-main mb-2">Energy Drain</h3>
                  <p className="text-text-muted">After writing for hours, you're too tired for revision, practice, or understanding concepts.</p>
                </div>
              </div>
            </div>

            <div className="bg-glass/20 border border-border-glow/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <BookOpenIcon className="w-8 h-8 text-text-main flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-text-main mb-2">Missed Understanding</h3>
                  <p className="text-text-muted">While busy writing, you're not actively processing or understanding the concepts being taught.</p>
                </div>
              </div>
            </div>

            <div className="bg-glass/20 border border-border-glow/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <DocumentTextIcon className="w-8 h-8 text-text-main flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-text-main mb-2">Syllabus Backlog</h3>
                  <p className="text-text-muted">Notes pile up, chapters remain incomplete, and exam stress keeps building up.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-l-4 border-red-500 rounded-r-xl p-6">
            <p className="text-lg font-semibold text-text-main mb-2">
              Here's the Reality:
            </p>
            <p className="text-text-muted leading-relaxed">
              Students spend <span className="text-red-400 font-bold">20+ hours every week</span> making notes instead of actually learning. 
              This leads to incomplete syllabus, accumulated backlogs, and last-minute exam panic.
            </p>
          </div>
        </div>
      </div>

      {/* The Solution Section */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 backdrop-blur-sm border border-primary/30 rounded-2xl p-8 mb-12">
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent flex items-center gap-3">
          <CheckCircleIcon className="w-10 h-10 text-green-400" />
          The Solution: NotesCrafter
        </h2>
        
        <p className="text-lg text-text-muted mb-6 leading-relaxed">
          A <span className="text-primary font-semibold">100% free tool</span> that instantly transforms dark lecture PDFs 
          from PW, Unacademy, eSaral, ALLEN, or any EdTech platform into clean, white background, print-ready notes.
        </p>

        <div className="grid md:grid-cols-3 gap-6 my-8">
          <div className="text-center p-6 bg-glass/20 rounded-xl border border-primary/30">
            <CheckCircleIcon className="w-12 h-12 text-primary mx-auto mb-3" />
            <div className="text-3xl font-bold text-primary mb-2">30 sec</div>
            <p className="text-text-muted">Average conversion time</p>
          </div>

          <div className="text-center p-6 bg-glass/20 rounded-xl border border-primary/30">
            <SparklesIcon className="w-12 h-12 text-primary mx-auto mb-3" />
            <div className="text-3xl font-bold text-primary mb-2">Free</div>
            <p className="text-text-muted">No cost, forever</p>
          </div>

          <div className="text-center p-6 bg-glass/20 rounded-xl border border-primary/30">
            <ClockIcon className="w-12 h-12 text-primary mx-auto mb-3" />
            <div className="text-3xl font-bold text-primary mb-2">20+ hrs</div>
            <p className="text-text-muted">Saved every week</p>
          </div>
        </div>
      </div>

      {/* Why This Tool Section */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <BookOpenIcon className="w-8 h-8 text-primary" />
          Why NotesCrafter Works Perfectly for Students
        </h2>

        <div className="space-y-6">
          <div className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6" />
              Compatible with ALL EdTech Platforms
            </h3>
            <p className="text-text-muted leading-relaxed">
              Whether you're using <strong>Physics Wallah (PW)</strong>, <strong>Unacademy</strong>, <strong>eSaral</strong>, 
              <strong> ALLEN</strong>, <strong>Vedantu</strong>, <strong>BYJU'S</strong>, or any other platform — 
              NotesCrafter handles all PDF formats seamlessly.
            </p>
          </div>

          <div className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6" />
              Perfect White Background for Printing
            </h3>
            <p className="text-text-muted leading-relaxed">
              Dark backgrounds consume excessive printer ink. NotesCrafter converts those dark slides into 
              <strong> clean white backgrounds</strong> that are perfect for printing and easy on your printer.
            </p>
          </div>

          <div className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
              <SparklesIcon className="w-6 h-6" />
              Familiar Handwriting & Layout = Better Memory
            </h3>
            <p className="text-text-muted leading-relaxed">
              When you watch lectures, the teacher's handwriting and slide layout get synced to your brain. 
              Having the <strong>same notes in print</strong> feels familiar and helps you recall concepts better during revision. 
              Your brain recognizes the patterns, making study sessions more effective!
            </p>
          </div>

          <div className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6" />
              Android App Free with Ads
            </h3>
            <p className="text-text-muted leading-relaxed">
              Download our Android app for <strong>completely free</strong> access with non-intrusive ads. 
              Web processing requires Community Pass (starts at ₹1). 
              Same powerful features on both platforms!
            </p>
          </div>

          <div className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6" />
              Complete Privacy Protection
            </h3>
            <p className="text-text-muted leading-relaxed">
              All processing happens <strong>directly in your browser</strong>. Your files never leave your device, 
              never uploaded to any server. Your notes remain completely private and secure.
            </p>
          </div>
        </div>
      </div>

      {/* How to Use Section */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 backdrop-blur-sm border border-primary/30 rounded-2xl p-8 mb-12">
        <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <DocumentTextIcon className="w-8 h-8 text-primary" />
          How to Convert Notes in 3 Simple Steps
        </h2>

        <div className="space-y-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-xl">
                1
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-primary mb-2">Upload Your PDF</h3>
              <p className="text-text-muted leading-relaxed">
                Click "Select Your PDF" or drag & drop your lecture notes from PW, Unacademy, eSaral, ALLEN, or any platform. 
                Files stay on your device — 100% private!
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-xl">
                2
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-primary mb-2">Configure Settings</h3>
              <p className="text-text-muted leading-relaxed mb-3">
                Choose your preferences:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-muted ml-4">
                <li><strong>Background:</strong> Select "White" for best printing results</li>
                <li><strong>Text Color:</strong> Choose "Black" or "Dark" for clear readability</li>
                <li><strong>Enhancement Level:</strong> Pick "High" for maximum clarity</li>
                <li><strong>Paper Size:</strong> Select A4 (standard size)</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-xl">
                3
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-primary mb-2">Download & Print</h3>
              <p className="text-text-muted leading-relaxed">
                Click "Enhance PDF" and wait 30 seconds. Download your optimized PDF with white background. 
                Now it's ready to print!
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => {
              const event = new CustomEvent('navigate', {
                detail: { page: 'home', startProcessing: true }
              });
              window.dispatchEvent(event);
            }}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
          >
            <PrinterIcon className="w-6 h-6" />
            <span>Start Converting Your Notes Now</span>
          </button>
        </div>
      </div>

      {/* Real Student Benefits */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <SparklesIcon className="w-8 h-8 text-primary" />
          Benefits You'll Experience
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-glass/20 border border-primary/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <ClockIcon className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-semibold text-primary">Save 20+ Hours Weekly</h3>
            </div>
            <ul className="space-y-2 text-text-muted">
              <li>• No manual note-making needed</li>
              <li>• Convert in 30 seconds vs 4 hours</li>
              <li>• More time for actual learning</li>
            </ul>
          </div>

          <div className="bg-glass/20 border border-primary/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <BookOpenIcon className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-semibold text-primary">Study More Effectively</h3>
            </div>
            <ul className="space-y-2 text-text-muted">
              <li>• Focus on understanding concepts</li>
              <li>• More time for practice problems</li>
              <li>• Complete syllabus without stress</li>
            </ul>
          </div>

          <div className="bg-glass/20 border border-primary/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <SparklesIcon className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-semibold text-primary">Better Memory Retention</h3>
            </div>
            <ul className="space-y-2 text-text-muted">
              <li>• Familiar handwriting aids recall</li>
              <li>• Recognized layout patterns</li>
              <li>• Faster concept recognition</li>
            </ul>
          </div>

          <div className="bg-glass/20 border border-primary/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircleIcon className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-semibold text-primary">Improved Performance</h3>
            </div>
            <ul className="space-y-2 text-text-muted">
              <li>• More energy for revision</li>
              <li>• Reduced stress and anxiety</li>
              <li>• Better exam preparation</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Common Questions */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8">Common Questions</h2>

        <div className="space-y-4">
          <details className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6 cursor-pointer group">
            <summary className="text-lg font-semibold text-text-main mb-2 cursor-pointer list-none flex items-center justify-between">
              <span>Does this work with Physics Wallah (PW) notes?</span>
              <span className="text-primary text-2xl group-open:rotate-180 transition-transform">›</span>
            </summary>
            <p className="text-text-muted leading-relaxed mt-4 border-t border-border-glow/20 pt-4">
              Yes! Absolutely. NotesCrafter works perfectly with all PW lecture PDFs. Whether it's Physics, Chemistry, Maths, 
              or Biology notes — convert them to white background instantly.
            </p>
          </details>

          <details className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6 cursor-pointer group">
            <summary className="text-lg font-semibold text-text-main mb-2 cursor-pointer list-none flex items-center justify-between">
              <span>Can I use this for Unacademy, eSaral, and ALLEN notes?</span>
              <span className="text-primary text-2xl group-open:rotate-180 transition-transform">›</span>
            </summary>
            <p className="text-text-muted leading-relaxed mt-4 border-t border-border-glow/20 pt-4">
              Yes! NotesCrafter supports PDFs from ALL EdTech platforms including Unacademy, eSaral, ALLEN, Vedantu, BYJU'S, 
              Khan Academy, and more. If it's a PDF, we can convert it!
            </p>
          </details>

          <details className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6 cursor-pointer group">
            <summary className="text-lg font-semibold text-text-main mb-2 cursor-pointer list-none flex items-center justify-between">
              <span>Is it really 100% free?</span>
              <span className="text-primary text-2xl group-open:rotate-180 transition-transform">›</span>
            </summary>
            <p className="text-text-muted leading-relaxed mt-4 border-t border-border-glow/20 pt-4">
              Yes, completely free! No hidden charges, no subscription plans, no "premium" features. 
              We believe education resources should be accessible to everyone. No credit card required, ever.
            </p>
          </details>

          <details className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6 cursor-pointer group">
            <summary className="text-lg font-semibold text-text-main mb-2 cursor-pointer list-none flex items-center justify-between">
              <span>Are my notes safe and private?</span>
              <span className="text-primary text-2xl group-open:rotate-180 transition-transform">›</span>
            </summary>
            <p className="text-text-muted leading-relaxed mt-4 border-t border-border-glow/20 pt-4">
              Your notes are 100% safe! Everything happens in YOUR browser. Your files NEVER leave your device. 
              We don't have servers to store files. Your privacy is our top priority.
            </p>
          </details>

          <details className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6 cursor-pointer group">
            <summary className="text-lg font-semibold text-text-main mb-2 cursor-pointer list-none flex items-center justify-between">
              <span>What if my PDF has handwritten notes or diagrams?</span>
              <span className="text-primary text-2xl group-open:rotate-180 transition-transform">›</span>
            </summary>
            <p className="text-text-muted leading-relaxed mt-4 border-t border-border-glow/20 pt-4">
              NotesCrafter handles all types of content — typed text, handwritten notes, diagrams, formulas, and images. 
              Our enhancement ensures everything remains clear and readable after conversion.
            </p>
          </details>

          <details className="bg-glass/20 backdrop-blur-sm border border-primary/20 rounded-xl p-6 cursor-pointer group">
            <summary className="text-lg font-semibold text-text-main mb-2 cursor-pointer list-none flex items-center justify-between">
              <span>How long does the conversion take?</span>
              <span className="text-primary text-2xl group-open:rotate-180 transition-transform">›</span>
            </summary>
            <p className="text-text-muted leading-relaxed mt-4 border-t border-border-glow/20 pt-4">
              Most PDFs convert in 20-60 seconds depending on the number of pages. 
              A typical 50-page lecture PDF takes about 30-40 seconds.
            </p>
          </details>
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 backdrop-blur-sm border border-primary/30 rounded-2xl p-8 mb-12">
        <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
          <SparklesIcon className="w-8 h-8 text-primary" />
          Pro Tips for Best Results
        </h2>

        <div className="space-y-4">
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
              1
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-1">Use High Enhancement</h3>
              <p className="text-text-muted">For lecture PDFs with dark backgrounds, always choose "High" enhancement level for the best conversion quality.</p>
            </div>
          </div>

          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
              2
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-1">Select A4 Paper Size</h3>
              <p className="text-text-muted">This is the standard size and makes your notes look professional and easy to organize.</p>
            </div>
          </div>

          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
              3
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-1">Batch Convert Multiple PDFs</h3>
              <p className="text-text-muted">Open multiple tabs and convert several PDFs simultaneously to save even more time!</p>
            </div>
          </div>

          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
              4
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-1">Print Double-Sided</h3>
              <p className="text-text-muted">Print on both sides to save paper and make your notes more compact and portable.</p>
            </div>
          </div>

          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
              5
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-1">Keep Digital Backups</h3>
              <p className="text-text-muted">Save both original and converted PDFs on Google Drive or your device for easy access anytime.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-center text-white mb-12">
        <h2 className="text-3xl font-bold mb-4">Ready to Save 20+ Hours Per Week?</h2>
        <p className="text-lg mb-6 opacity-90">
          Stop wasting time making notes. Start studying smarter, not harder.
        </p>
        <button
          onClick={() => {
            const event = new CustomEvent('navigate', {
              detail: { page: 'home', startProcessing: true }
            });
            window.dispatchEvent(event);
          }}
          className="inline-flex items-center gap-3 px-10 py-5 bg-white text-primary font-bold text-xl rounded-xl shadow-2xl hover:scale-105 transform transition-all duration-300"
        >
          <PrinterIcon className="w-7 h-7" />
          <span>Convert Your First PDF Now!</span>
        </button>
        <p className="text-sm mt-4 opacity-75">
          Android app free with ads • Web requires Community Pass • 100% Private
        </p>
      </div>

      {/* Conclusion */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-6">Conclusion</h2>
        <p className="text-text-muted leading-relaxed text-lg mb-4">
          Whether you're preparing for JEE, NEET, Board Exams, or any competitive exam, NotesCrafter is your perfect study companion. 
          Convert lecture PDFs from Physics Wallah, Unacademy, eSaral, ALLEN, or any other platform into clean, printable notes in seconds.
        </p>
        <p className="text-text-muted leading-relaxed text-lg">
          <strong className="text-text-main">Save 20+ hours every week. Study better. Learn faster.</strong> Join thousands of students who are already using NotesCrafter 
          to transform their study routine. Start converting your notes today — it's completely free, forever!
        </p>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 pt-8 border-t border-border-glow/30">
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#PhysicsWallah</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#PW</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#Unacademy</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#eSaral</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#ALLEN</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#JEE</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#NEET</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#StudyTips</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#FreeTool</span>
        <span className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium">#EdTech</span>
      </div>
    </article>
  );
};

export default PrintPWNotes;
